// Optimistic-concurrency helper. Pair it with the `version` column added by
// migration 055: every UPDATE matches on the version the client read, so a
// stale write hits zero rows and we can prompt the operator to refresh
// instead of silently overwriting another user's change.
//
// Usage:
//   const updated = await updateWithVersion(
//     'pallets',
//     { col: 'pallet_id', value: palletId },
//     pallet.version,
//     { status: 'IN_COLDROOM', coldroom_grid: 'A3' }
//   )
//   // updated.version is now bumped by the DB trigger (no need to set it client-side)

import { type SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

// Structural shape we use from the client. Both the main shared
// supabase client and the useSaveDb isolated client satisfy this — the
// helper doesn't care which one is wired in.
type SupabaseLike = Pick<SupabaseClient, 'from'>

// ---------------------------------------------------------------------------
// Typed error so mutations can recognise stale-write failures and prompt
// the user to refresh, instead of just showing a generic "failed" toast.
// ---------------------------------------------------------------------------
export class StaleWriteError extends Error {
  constructor(
    public readonly table: string,
    public readonly pkValue: string | number,
    public readonly attemptedVersion: number,
  ) {
    super(`Stale write on ${table} — someone else changed this record since you opened it. Refresh and try again.`)
    this.name = 'StaleWriteError'
  }
}

export function isStaleWriteError(err: unknown): err is StaleWriteError {
  return err instanceof StaleWriteError
}

// ---------------------------------------------------------------------------
// Distinct from a stale write: the UPDATE matched ZERO rows but the row STILL
// carries the version we matched on — so the WHERE matched and the write was
// *blocked*, not out-raced. The usual cause is an RLS policy (wrong tenant or
// too-low active-tenant role): an RLS UPDATE-deny returns 0 rows with NO error,
// which is otherwise indistinguishable from a version conflict. Surfacing this
// separately stops "you don't have permission" being mis-reported to the user
// (and paged to the team) as "someone else changed this record".
// ---------------------------------------------------------------------------
export class WriteBlockedError extends Error {
  constructor(
    public readonly table: string,
    public readonly pkValue: string | number,
  ) {
    super(`Update to ${table} was blocked — you may not have permission to change this record. No changes were saved.`)
    this.name = 'WriteBlockedError'
  }
}

export function isWriteBlockedError(err: unknown): err is WriteBlockedError {
  return err instanceof WriteBlockedError
}

// A SECURITY-INVOKER/DEFINER RPC that does its own optimistic-concurrency check
// (`WHERE version = p_expected_version`) signals a conflict by raising ERRCODE
// 'OPTLK' (codebase convention, mig 577 set_pallet_ssc). At the RPC call site,
// map it to the same StaleWriteError the table-UPDATE path throws so onError's
// isStaleWriteError() shows the "someone else changed this, refresh" prompt:
//   const { error } = await db.rpc('edit_spray_record', {...})
//   if (error) { if (isOptlk(error)) throw new StaleWriteError(t, pk, v); throw error }
export function isOptlk(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === 'OPTLK'
}

// ---------------------------------------------------------------------------
// Update a single row with a version check. Returns the row after update (with
// the bumped version). If no row matched it throws StaleWriteError (someone
// else bumped the version), WriteBlockedError (the row exists at the expected
// version but RLS denied the write), or a plain Error (the row is gone).
// ---------------------------------------------------------------------------
export async function updateWithVersion<T extends Record<string, unknown>>(
  table:    string,
  pk:       { col: string; value: string | number },
  version:  number,
  patch:    Record<string, unknown>,
  // opts.db lets the caller swap in an isolated client (useSaveDb) so
  // the update + the probe both bypass navigator.locks on busy pages
  // where the main supabase client's auth-token reads can stall a
  // mutation for minutes. Defaults to the shared client for backwards
  // compatibility with the 37+ existing callers.
  // opts.match adds equality filters to the UPDATE's WHERE beside (pk,
  // version) — a server-side precondition such as `{ status: 'DRAFT' }`. A row
  // that fails it matches nothing and is classified by the probe below like
  // any other 0-row result (never a success).
  opts?:    { select?: string; db?: SupabaseLike; match?: Record<string, string | number | boolean> },
): Promise<T> {
  const client = opts?.db ?? supabase
  let q = client
    .from(table)
    .update(patch)
    .eq(pk.col, pk.value)
    .eq('version', version)
  for (const [col, value] of Object.entries(opts?.match ?? {})) q = q.eq(col, value)
  const { data, error } = await q.select(opts?.select ?? '*')

  if (error) throw error

  // Zero rows matched. The DB updated nothing — three distinct causes, told
  // apart by probing the row's current version:
  //   • row vanished          → probe is null
  //   • write blocked by RLS  → probe.version === version (the row STILL holds
  //     the version we matched on, so the WHERE matched and the write was denied,
  //     not out-raced). RLS UPDATE-deny returns 0 rows with no error, so without
  //     this check it masquerades as a stale write.
  //   • genuine stale write   → probe.version !== version (someone bumped it)
  if (!data || data.length === 0) {
    const { data: probe } = await client
      .from(table).select('version').eq(pk.col, pk.value).maybeSingle()
    if (!probe) {
      throw new Error(`Row on ${table} no longer exists (was it deleted?).`)
    }
    if ((probe as { version?: number }).version === version) {
      throw new WriteBlockedError(table, pk.value)
    }
    throw new StaleWriteError(table, pk.value, version)
  }

  return data[0] as unknown as T
}
